import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { User, UserRole, Sede } from '../types';
import api from '../utils/api';
import { SedeService, UserService } from '../services/apiService';
import axios from 'axios';
import { getSediGestiteIds, resolveSedeGestitaIniziale } from '../utils/sediGestite';

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    currentSedeId: number | 'ALL' | null;
    isLoading: boolean;
    login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
    logout: () => void;
    selectSede: (sedeId: number | 'ALL') => void;
    refreshSedi: () => Promise<void>;
    // Sedi selezionabili per la gestione: tutte per il titolare, solo quelle
    // gestite per il responsabile, quelle di appartenenza per il collaboratore.
    sedi: Sede[];
    // Id delle sedi gestite dal responsabile (vuoto per gli altri ruoli).
    sediGestiteIds: number[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [currentSedeId, setCurrentSedeId] = useState<number | 'ALL' | null>(null);
    const [sedi, setSedi] = useState<Sede[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const sediGestiteIds = useMemo(() => getSediGestiteIds(user), [user]);

    const normalizeRole = (backendRole: string): UserRole => {
        if (backendRole === 'COLLABORATOR') return UserRole.COLLABORATORE;
        return backendRole as UserRole;
    };

    const refreshSedi = async () => {
        if (!user) return;
        if (user.role === UserRole.ADMIN) {
            try {
                const res = await SedeService.getAll();
                if (res.success) {
                    const data = Array.isArray(res) ? res : (res.data || []);
                    setSedi(data as Sede[]);
                }
            } catch (error) {
                console.error('Errore refresh sedi:', error);
            }
            return;
        }
        // Responsabile/collaboratore: il payload contiene già `user.sedi`
        // con id e nome di tutte le sedi di appartenenza. Il responsabile
        // gestisce solo le sedi in `sediGestiteIds`.
        if (user.sedi && user.sedi.length > 0) {
            const all = user.sedi as unknown as Sede[];
            if (user.role === UserRole.RESPONSABILE) {
                const gestite = getSediGestiteIds(user);
                setSedi(all.filter(s => gestite.some(id => String(id) === String(s.id))));
            } else {
                setSedi(all);
            }
            return;
        }
        // Fallback per dati vecchi con il solo user.sedeId.
        if (user.sedeId) {
            try {
                const res = await SedeService.getById(user.sedeId);
                if (res.success && res.data) {
                    setSedi([res.data as Sede]);
                }
            } catch (error) {
                console.error('Errore caricamento sede utente:', error);
            }
        }
    };

    useEffect(() => {
        if (user) {
            refreshSedi();
        } else {
            setSedi([]);
        }
    }, [user]);

    // Ripristino sessione al caricamento della pagina
    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

            if (!token) {
                // Nessun token salvato: nessuna sessione da ripristinare
                setIsLoading(false);
                return;
            }

            try {
                const response = await UserService.checkToken();

                if (response.success && response.data) {
                    const userData = response.data;
                    setUser(userData);

                    if (userData.role === UserRole.ADMIN) {
                        const storedSedeId = localStorage.getItem('currentSedeId');
                        setCurrentSedeId(
                            storedSedeId
                                ? storedSedeId === 'ALL' ? 'ALL' : Number(storedSedeId)
                                : 'ALL'
                        );
                    } else if (userData.role === UserRole.RESPONSABILE) {
                        setCurrentSedeId(resolveSedeGestitaIniziale(userData, localStorage.getItem('currentSedeId')));
                    } else {
                        setCurrentSedeId(userData.sedeId || null);
                    }
                } else {
                    // Il backend risponde ma la sessione non è valida
                    logout();
                }
            } catch (error) {
                // Logout solo su 401. Su errori di rete o 5xx la sessione resta:
                // il backend potrebbe essere solo momentaneamente irraggiungibile.
                if (axios.isAxiosError(error) && error.response?.status === 401) {
                    console.warn('Token scaduto o non valido — logout.');
                    logout();
                } else {
                    console.error('Errore di rete in initAuth (sessione mantenuta):', error);
                    // Fallback: utente salvato in localStorage
                    const stored = localStorage.getItem('authUser') || sessionStorage.getItem('authUser');
                    if (stored) {
                        try {
                            const storedUser: User = JSON.parse(stored);
                            setUser(storedUser);
                            const storedSedeId = localStorage.getItem('currentSedeId');
                            if (storedUser.role === UserRole.RESPONSABILE) {
                                setCurrentSedeId(resolveSedeGestitaIniziale(storedUser, storedSedeId));
                            } else {
                                setCurrentSedeId(
                                    storedSedeId
                                        ? storedSedeId === 'ALL' ? 'ALL' : Number(storedSedeId)
                                        : 'ALL'
                                );
                            }
                        } catch { /* localStorage corrotto */ }
                    }
                }
            } finally {
                setIsLoading(false);
            }
        };

        initAuth();
    }, []);

    const login = async (email: string, password: string, rememberMe: boolean = false) => {
        try {
            // Il BE richiede `source` (WEB/APP) nel login, altrimenti risponde 400;
            // il valore finisce nel claim `src` del JWT.
            const response = await api.post('/auth/public/login', { email, password, source: 'WEB' });
            const userDataFromBackend = response.data.data;

            if (!userDataFromBackend || !userDataFromBackend.token) {
                throw new Error('Token mancante nella risposta del server');
            }

            const userData: User = {
                id: userDataFromBackend.id,
                email: userDataFromBackend.email,
                name: userDataFromBackend.name || userDataFromBackend.nome || '',
                surname: userDataFromBackend.surname || userDataFromBackend.cognome || '',
                role: normalizeRole(userDataFromBackend.role),
                sedeId: userDataFromBackend.sedeId,
                sedi: userDataFromBackend.sedi,
                sediGestiteIds: userDataFromBackend.sediGestiteIds,
                whatsappOptIn: userDataFromBackend.whatsappOptIn,
                whatsappEnabled: userDataFromBackend.whatsappEnabled,
                phone: userDataFromBackend.phone,
            };

            const storage = rememberMe ? localStorage : sessionStorage;
            storage.setItem('authToken', userDataFromBackend.token);
            storage.setItem('authUser', JSON.stringify(userData));
            setUser(userData);

            if (userData.role === UserRole.ADMIN) {
                setCurrentSedeId('ALL');
                storage.setItem('currentSedeId', 'ALL');
            } else if (userData.role === UserRole.RESPONSABILE) {
                setCurrentSedeId(resolveSedeGestitaIniziale(userData));
            } else {
                setCurrentSedeId(userData.sedeId || null);
            }
        } catch (error) {
            console.error('Login fallito:', error);
            throw error;
        }
    };

    const logout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        localStorage.removeItem('currentSedeId');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('authUser');
        sessionStorage.removeItem('currentSedeId');
        setUser(null);
        setCurrentSedeId(null);
        if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
        }
    };

    const selectSede = (sedeId: number | 'ALL') => {
        if (user?.role === UserRole.ADMIN) {
            setCurrentSedeId(sedeId);
            localStorage.setItem('currentSedeId', String(sedeId));
            return;
        }
        // Il responsabile può scegliere solo tra le sedi che gestisce, senza "ALL".
        if (user?.role === UserRole.RESPONSABILE && sedeId !== 'ALL') {
            if (getSediGestiteIds(user).some(id => String(id) === String(sedeId))) {
                setCurrentSedeId(sedeId);
                localStorage.setItem('currentSedeId', String(sedeId));
            }
        }
    };

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated: !!user,
            currentSedeId,
            sedi,
            sediGestiteIds,
            refreshSedi,
            login,
            logout,
            selectSede,
            isLoading,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth deve essere usato dentro un AuthProvider');
    return context;
};